import { Injectable, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, catchError, forkJoin, interval, map, of } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { MessageService } from './message.service';
import { StaffMessageEventsService } from './staff-message-events.service';
import { StaffMessageService } from './staff-message.service';

/** Don't ask the backend more often than this, however many requests the app makes in between. */
const MIN_CHECK_INTERVAL_MS = 15_000;

/** How often to look for new messages while the app is open and visible - even if the user does nothing else. */
const POLL_INTERVAL_MS = 30_000;

const KNOWN_IDS_KEY = 'cba.knownUnreadMessageIds';

const STAFF_ROLES = ['DRIVER', 'COLLECTOR', 'OFFICER', 'PLANNER', 'CONTROLLER', 'MANAGER', 'ADMIN'];

/**
 * Tells every user about new messages concerning them, whatever the source (customer thread, system mail,
 * colleague, tour message, shared staff queue): the unread count for the header badge, and a persistent "Neue
 * Nachrichten vorhanden" snackbar. There is no push channel, so this asks the backend for the ids of the user's
 * unread messages (own log, plus the staff queue for staff roles) - on a 30 s timer while the tab is visible,
 * right when the tab becomes visible again (a phone coming back from the pocket), after ordinary traffic
 * (authInterceptor calls checkSoon() after every successful response) and whenever this tab read or settled a
 * message itself. Every trigger is throttled to one request per 15 s.
 * <p>
 * The snackbar has no timeout and only informs - messages stay unread until opened. It appears only for ids not
 * seen before: the ids known when it was shown (or dismissed, also by being replaced by another snackbar) are
 * remembered in localStorage, so reloads don't bring it back. The first check after login only sets the baseline
 * (the badge covers what is already unread).
 */
@Injectable({
  providedIn: 'root'
})
export class NewMessageAlertService {
  private readonly authService = inject(AuthService);
  private readonly messageService = inject(MessageService);
  private readonly staffMessageService = inject(StaffMessageService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly events = inject(StaffMessageEventsService);

  /** Unread messages for the header badge; 0 while logged out. */
  readonly unreadCount = signal(0);

  /** Fires whenever a check finds a different set of unread messages than the one before (new arrivals, or
   * messages read elsewhere) - for views that show per-message state and should refresh themselves. */
  private readonly unreadChanged = new Subject<void>();
  readonly unreadChanged$ = this.unreadChanged.asObservable();

  private lastCheck = 0;
  private checking = false;
  private snackBarOpen = false;
  private known: Set<string> | null = this.loadKnown();
  private latestIds: string[] = [];

  constructor() {
    interval(POLL_INTERVAL_MS).subscribe(() => {
      if (document.visibilityState === 'visible') {
        this.checkSoon();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkSoon();
      }
    });
    // This tab read/confirmed/answered something: the count changed, no need to wait for the timer.
    this.events.changed$.subscribe(() => this.checkSoon(true));
  }

  /** `force` skips the throttle - for our own actions, which should show up in the badge at once. */
  checkSoon(force = false): void {
    if (!this.authService.isLoggedIn()) {
      this.unreadCount.set(0);
      this.updateAppBadge(0);
      this.known = null;
      this.saveKnown();
      return;
    }
    if (this.checking) {
      return;
    }
    const now = Date.now();
    if (!force && now - this.lastCheck < MIN_CHECK_INTERVAL_MS) {
      return;
    }
    this.lastCheck = now;
    this.checking = true;

    const isStaff = STAFF_ROLES.some(role => this.authService.hasRole(role));
    forkJoin([
      this.messageService.unreadIds().pipe(catchError(() => of([] as string[]))),
      isStaff ? this.staffMessageService.unreadIds().pipe(catchError(() => of([] as string[]))) : of([] as string[])
    ]).pipe(map(([own, staff]) => [...new Set([...own, ...staff])])).subscribe(ids => {
      this.checking = false;
      this.unreadCount.set(ids.length);
      this.updateAppBadge(ids.length);
      this.show(ids);
    });
  }

  /** The number on the app icon (installed web app, where the platform has one) follows the unread count. A push
   * sets it too (push-sw.js) - this is what takes it away again once the messages have been read. */
  private updateAppBadge(count: number): void {
    const nav = navigator as Navigator & { setAppBadge?: (count: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    try {
      void (count > 0 ? nav.setAppBadge?.(count) : nav.clearAppBadge?.())?.catch(() => undefined);
    } catch {
      // no app badge support
    }
  }

  private show(ids: string[]): void {
    const changed = ids.length !== this.latestIds.length || ids.some(id => !this.latestIds.includes(id));
    this.latestIds = ids;
    if (changed) {
      this.unreadChanged.next();
    }
    if (this.known === null) {
      // First check without stored state: what is unread right now is not "new".
      this.known = new Set(ids);
      this.saveKnown();
      return;
    }
    const isNew = ids.some(id => !this.known!.has(id));
    // Forget ids that are no longer unread, so the stored set doesn't grow.
    this.known = new Set([...this.known].filter(id => ids.includes(id)));
    if (this.snackBarOpen || !isNew) {
      this.saveKnown();
      return;
    }
    this.snackBarOpen = true;
    this.snackBar.open('Neue Nachrichten vorhanden', 'Ok')
      .afterDismissed().subscribe(() => {
        this.snackBarOpen = false;
        this.latestIds.forEach(id => this.known?.add(id));
        this.saveKnown();
      });
  }

  private loadKnown(): Set<string> | null {
    try {
      const raw = localStorage.getItem(KNOWN_IDS_KEY);
      return raw === null ? null : new Set<string>(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  private saveKnown(): void {
    try {
      if (this.known === null) {
        localStorage.removeItem(KNOWN_IDS_KEY);
      } else {
        localStorage.setItem(KNOWN_IDS_KEY, JSON.stringify([...this.known]));
      }
    } catch {
      // storage unavailable - the alert then just follows the in-memory state
    }
  }
}
